import React, { useEffect, useRef } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

const StyledEducationSection = styled.section`
  max-width: 900px;
`;

const StyledTimeline = styled.div`
  position: relative;
  padding-left: 28px;

  &::before {
    content: '';
    position: absolute;
    left: 5px;
    top: 12px;
    bottom: 20px;
    width: 2px;
    background: linear-gradient(to bottom, #f97316 0%, var(--lightest-navy) 100%);
  }

  @media (max-width: 600px) {
    padding-left: 20px;
  }
`;

const StyledTimelineItem = styled.div`
  position: relative;
  margin-bottom: 48px;

  &:last-of-type {
    margin-bottom: 0;
  }

  &::before {
    content: '';
    position: absolute;
    left: -34px;
    top: 10px;
    width: 12px;
    height: 12px;
    border-radius: 2px;
    transform: rotate(45deg);
    border: 2px solid #f97316;
    background: var(--navy);
    z-index: 1;
  }

  @media (max-width: 600px) {
    &::before {
      left: -26px;
    }
  }

  .item-header {
    margin-bottom: 10px;
  }

  .school-line {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;
  }

  .school-name {
    color: #f97316;
    font-family: var(--font-mono);
    font-size: var(--fz-sm);
    font-weight: 500;

    a {
      color: #f97316;
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  .item-title {
    color: var(--lightest-slate);
    font-size: var(--fz-xxl);
    font-weight: 500;
    line-height: 1.3;
    margin: 0 0 4px;
  }

  .item-department {
    color: var(--slate);
    font-family: var(--font-mono);
    font-size: var(--fz-xs);
    margin-bottom: 4px;
  }

  .item-range {
    color: var(--light-slate);
    font-family: var(--font-mono);
    font-size: var(--fz-xs);
    margin: 0;
  }

  .item-body {
    margin-top: 8px;
    color: var(--slate);
    font-size: var(--fz-sm);

    p {
      margin: 0;
    }
  }
`;

const Education = () => {
  const data = useStaticQuery(graphql`
    query {
      education: allMarkdownRemark(
        filter: { fileAbsolutePath: { regex: "/content/education/" } }
        sort: { fields: [frontmatter___date], order: DESC }
      ) {
        edges {
          node {
            frontmatter {
              title
              school
              department
              range
              url
            }
            html
          }
        }
      }
    }
  `);

  const educationData = data.education.edges;
  const revealContainer = useRef(null);
  const revealItems = useRef([]);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }
    sr.reveal(revealContainer.current, srConfig());
    revealItems.current.forEach((ref, i) => sr.reveal(ref, srConfig(i * 100)));
  }, []);

  return (
    <StyledEducationSection id="education" ref={revealContainer}>
      <h2 className="numbered-heading">Education</h2>

      <StyledTimeline>
        {educationData &&
          educationData.map(({ node }, i) => {
            const { frontmatter, html } = node;
            const { title, school, department, range, url } = frontmatter;

            return (
              <StyledTimelineItem key={i} ref={el => (revealItems.current[i] = el)}>
                <div className="item-header">
                  <div className="school-line">
                    <span className="school-name">
                      <a href={url} target="_blank" rel="noreferrer">
                        {school}
                      </a>
                    </span>
                  </div>
                  <h3 className="item-title">{title}</h3>
                  {department && <p className="item-department">{department}</p>}
                  <p className="item-range">{range}</p>
                </div>

                {html && html.replace(/<[^>]*>/g, '').trim() && (
                  <div className="item-body" dangerouslySetInnerHTML={{ __html: html }} />
                )}
              </StyledTimelineItem>
            );
          })}
      </StyledTimeline>
    </StyledEducationSection>
  );
};

export default Education;
